import * as React from "react";
import { Button, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import { LocalUser } from "@preveil-api";
import { AppConfiguration, PublicRoutes, QueryParamKeys, QueryParamValues } from "src/common";
import { Message } from "src/common/app/message-item.class";
import { MessageHandlerDisplayType } from "src/common/constants/constants";
import { DeveloperErrorMessages, DeveloperSuccessMessages, DeveloperWarningMessages, useAddOrgUser } from "src/common/developers";
import { uiActions } from "src/store";
import * as yup from "yup";

type AllProps = {
  admin_user: LocalUser;
  account_list: LocalUser[];
}

function AddExistingUserFormComponent(props: AllProps) {
  const { admin_user, account_list } = props;
  const default_department = "Dept";
  const { addExistingMember, success, setSuccess } = useAddOrgUser();
  const dispatch = useDispatch();

  const schema = yup.object().shape({
    user_id: yup.string().required("User selection is required"),
    department: yup.string().required("Department is required"),
  });

  function handleError(error: any) {
    switch (error.status) {
      case 400:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_user_already_in_org, MessageHandlerDisplayType.toastr)));
        break;
      default:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_adding_user, MessageHandlerDisplayType.toastr)));
        break;
    }
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={{ user_id: "", department: default_department }}
      onSubmit={async (values, { setSubmitting }) => {
        const user_id = values.user_id;
        const department = values.department;
        const isWeb = AppConfiguration.buildForWeb();
        if (isWeb) {
          dispatch(uiActions.handleRequestErrors(new Message(DeveloperWarningMessages.warning_wrong_port, MessageHandlerDisplayType.toastr)));
          return;
        }
        try {
          await addExistingMember(admin_user.user_id, admin_user.org_info?.org_id || "", user_id, department);
          setSuccess(true);
          dispatch(uiActions.handleSetMessage(new Message(DeveloperSuccessMessages.success_adding_user)));
        } catch (error: any) {
          handleError(error);
        }
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="user_id">Select User</Form.Label>
            <Form.Select
              id="user_id"
              name="user_id"
              value={values.user_id}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.user_id && touched.user_id}
            >
              <option value="">Choose a user...</option>
              {account_list.map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.user_id} ({user.display_name})
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.user_id}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="department">Department</Form.Label>
            <Form.Control
              id="department"
              name="department"
              type="text"
              placeholder="Dept"
              value={values.department}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.department && touched.department}
            />
            <Form.Control.Feedback type="invalid">
              {errors.department}
            </Form.Control.Feedback>
          </Form.Group>
          <Button type="submit" className="me-2" disabled={isSubmitting}>Add User</Button>
          {success && (
            <Button 
              variant="link" 
              href={`/${PublicRoutes.choose_account_route}?${QueryParamKeys.PAGE_ACTION_QUERY_KEY}=${QueryParamValues.switch}`}
            >
              Choose Account
            </Button>
          )}
        </Form>
      )}
    </Formik>
  );
}

export default React.memo(AddExistingUserFormComponent);
